import { useTranslation } from 'react-i18next'

import { Box } from '@enechain/ecloud-designsystem'
import { FaTriangleExclamationSolid } from '@enechain/ecloud-icons'

import { iconSize } from '~/constants/iconSize'

/**
 * エリアタブ / 日断面タブのインジケータ (AC-PSM-013 / 014 / 015、Design Note
 * `submission-tab-indicators`)。ミスマッチ (取引相手間ミスマッチ) と未提出は
 * 独立に重畳する。補足は title (hover) と visually-hidden テキスト (radio の
 * accessible name = キーボード / SR) の両方で出す。クリックは付けず、タブ選択
 * のまま (Decision 10)。
 */

/**
 * 1 日断面ぶんの件数 (計画単位)。
 * 各 component の prop が `undefined` の時は、その日が取得中 / 取得失敗で不明
 * なので出さない。
 */
export type DayIndicatorCounts = {
  unsubmittedPlanCount: number
  /** 取引相手間ミスマッチ・差戻し (proto `counterpartyMismatchOrRejectedPlanCount`) */
  counterpartyMismatchOrRejectedPlanCount: number
  /** 計画内不整合 (proto `internalInconsistencyPlanCount`、AC-PSM-015)。未提出・赤の印と独立 */
  internalInconsistencyPlanCount: number
}

const VISUALLY_HIDDEN_CSS = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
} as const

const HiddenText = ({
  children,
}: {
  children: React.ReactNode
}): React.ReactElement => (
  <Box as="span" css={VISUALLY_HIDDEN_CSS}>
    {children}
  </Box>
)

/**
 * タブの円形マーク。`icon` = エリアタブの glyph、`count` = 日断面タブの件数 badge。
 * tone で色を引く。negative = 取引相手間ミスマッチ (赤)、warning = 計画内不整合 (AC-PSM-015)。
 * 計画内不整合は警告系の地 + 枠 (赤とも「未提出」の枠なしの角丸 flag とも分ける) に、
 * カードヘッダーの「計画内不整合」バッジと同じ DS の `FaTriangleExclamationSolid` を置き、
 * タブの印とバッジを同じものとして読ませる (#9865 Des 判断。design
 * `plan/submission.html` の `.area-internal-mark` / `.internal-count-badge`)。
 */
const MARK_TONES = {
  negative: {
    color: 'Text.highEmphasisInverse',
    background: 'Surface.negativeHigh',
    borderWidth: '0',
    borderColor: 'transparent',
  },
  warning: {
    color: 'Text.warningHigh',
    background: 'Surface.warningLow',
    borderWidth: '1px',
    borderColor: 'Border.warningMid',
  },
} as const

const IndicatorMark = ({
  tone,
  detail,
  shape,
  children,
}: {
  tone: keyof typeof MARK_TONES
  detail: string
  shape: 'icon' | 'count'
  children: React.ReactNode
}): React.ReactElement => (
  <>
    <Box
      as="span"
      textStyle="captionStrong"
      alignItems="center"
      justifyContent="center"
      gap="2px"
      display="inline-flex"
      {...(shape === 'icon'
        ? { width: '18px' }
        : tone === 'warning'
          ? // 件数の前に icon を置く pill は、icon 側の余白を詰める (design `.internal-count-badge` の padding 0 6px 0 4px)
            { minWidth: '18px', paddingLeft: '4px', paddingRight: '6px' }
          : { minWidth: '18px', paddingX: '5px' })}
      height="18px"
      marginLeft="6px"
      {...MARK_TONES[tone]}
      borderRadius="full"
      aria-hidden="true"
      title={detail}
    >
      {children}
    </Box>
    <HiddenText>{detail}</HiddenText>
  </>
)

/** エリアタブ: 当日∪翌日。補足に当日 / 翌日の内訳 (不明な日は内訳から除く)。 */
export const AreaTabIndicators = ({
  today,
  tomorrow,
}: {
  today: DayIndicatorCounts | undefined
  tomorrow: DayIndicatorCounts | undefined
}): React.ReactElement | null => {
  const { t } = useTranslation()
  const days = [
    { key: 'today', counts: today },
    { key: 'tomorrow', counts: tomorrow },
  ] as const

  const detail = (
    label: string,
    pick: (counts: DayIndicatorCounts) => number,
  ): string =>
    t('page:submissionManagement.tabIndicator.areaDetail', {
      label,
      counts: days
        .flatMap(({ key, counts }) =>
          counts === undefined
            ? []
            : [
                t(`page:submissionManagement.tabIndicator.count.${key}`, {
                  count: pick(counts),
                }),
              ],
        )
        .join(' / '),
    })

  const hasMismatch = days.some(
    ({ counts }) => (counts?.counterpartyMismatchOrRejectedPlanCount ?? 0) > 0,
  )
  const hasUnsubmitted = days.some(
    ({ counts }) => (counts?.unsubmittedPlanCount ?? 0) > 0,
  )
  const hasInternal = days.some(
    ({ counts }) => (counts?.internalInconsistencyPlanCount ?? 0) > 0,
  )
  if (!hasMismatch && !hasUnsubmitted && !hasInternal) {
    return null
  }

  const mismatchDetail = detail(
    t('page:submissionManagement.tabIndicator.mismatch'),
    (c) => c.counterpartyMismatchOrRejectedPlanCount,
  )
  const unsubmittedDetail = detail(
    t('page:submissionManagement.tabIndicator.unsubmittedPlans'),
    (c) => c.unsubmittedPlanCount,
  )
  const internalDetail = detail(
    t('page:submissionManagement.tabIndicator.internalInconsistency'),
    (c) => c.internalInconsistencyPlanCount,
  )

  return (
    <>
      {hasMismatch && (
        <IndicatorMark detail={mismatchDetail} shape="icon" tone="negative">
          !
        </IndicatorMark>
      )}
      {hasInternal && (
        <IndicatorMark detail={internalDetail} shape="icon" tone="warning">
          <FaTriangleExclamationSolid boxSize={iconSize.badgeIcon} />
        </IndicatorMark>
      )}
      {hasUnsubmitted && (
        <UnsubmittedFlag
          hiddenText={unsubmittedDetail}
          title={unsubmittedDetail}
        />
      )}
    </>
  )
}

const UnsubmittedFlag = ({
  title,
  hiddenText,
}: {
  title: string
  hiddenText: string
}): React.ReactElement => {
  const { t } = useTranslation()
  return (
    <>
      <Box
        as="span"
        textStyle="captionStrong"
        alignItems="center"
        display="inline-flex"
        height="18px"
        marginLeft="6px"
        color="Text.warningHigh"
        background="Surface.warningLow"
        borderRadius="sm"
        aria-hidden="true"
        paddingX="6px"
        title={title}
      >
        {t('page:submissionManagement.tabIndicator.unsubmitted')}
      </Box>
      <HiddenText>{hiddenText}</HiddenText>
    </>
  )
}

/** 日断面タブ: 表示中エリアのその日だけ。ミスマッチは件数 badge。 */
export const DayTabIndicators = ({
  counts,
}: {
  counts: DayIndicatorCounts | undefined
}): React.ReactElement | null => {
  const { t } = useTranslation()
  if (counts === undefined) {
    return null
  }
  const {
    unsubmittedPlanCount,
    counterpartyMismatchOrRejectedPlanCount,
    internalInconsistencyPlanCount,
  } = counts
  if (
    unsubmittedPlanCount === 0 &&
    counterpartyMismatchOrRejectedPlanCount === 0 &&
    internalInconsistencyPlanCount === 0
  ) {
    return null
  }
  const mismatchDetail = t('page:submissionManagement.tabIndicator.dayDetail', {
    label: t('page:submissionManagement.tabIndicator.mismatch'),
    count: counterpartyMismatchOrRejectedPlanCount,
  })
  const unsubmittedDetail = t(
    'page:submissionManagement.tabIndicator.dayDetail',
    {
      label: t('page:submissionManagement.tabIndicator.unsubmittedPlans'),
      count: unsubmittedPlanCount,
    },
  )
  const internalDetail = t('page:submissionManagement.tabIndicator.dayDetail', {
    label: t('page:submissionManagement.tabIndicator.internalInconsistency'),
    count: internalInconsistencyPlanCount,
  })
  return (
    <>
      {counterpartyMismatchOrRejectedPlanCount > 0 && (
        <IndicatorMark detail={mismatchDetail} shape="count" tone="negative">
          {counterpartyMismatchOrRejectedPlanCount}
        </IndicatorMark>
      )}
      {internalInconsistencyPlanCount > 0 && (
        <IndicatorMark detail={internalDetail} shape="count" tone="warning">
          <FaTriangleExclamationSolid boxSize={iconSize.countPillIcon} />
          {internalInconsistencyPlanCount}
        </IndicatorMark>
      )}
      {unsubmittedPlanCount > 0 && (
        <UnsubmittedFlag
          hiddenText={unsubmittedDetail}
          title={unsubmittedDetail}
        />
      )}
    </>
  )
}
